import { useCallback, useEffect, useMemo } from 'react'
import { useClearRefinements, useRefinementList } from 'react-instantsearch'
import { useIntl } from 'react-intl'
import type { ActionMeta, MultiValue } from 'react-select'
import { faTags } from '@fortawesome/pro-solid-svg-icons'
import type { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList'

import { Label } from 'Components/_theme'
import { Select } from 'Components/Select/Select'

import { getRentalsDeviceCategoryTranslationId, scrollToTop } from 'Utilities'

import { useCurrentRefinement } from 'Algolia/hooks/useCurrentRefinement/useCurrentRefinement'

import {
  CORE_SET,
  RENTALS_DEVICE_CATEGORY_CORE_VALUES,
  RENTALS_DEVICE_CATEGORY_OTHER_VALUE,
} from './DeviceCategoryFilter.constants'
import type { DeviceCategoryFilterProps } from './DeviceCategoryFilter.types'
import { getNonCoreValues } from './DeviceCategoryFilter.utils'

export const DeviceCategoryFilter = (props: DeviceCategoryFilterProps) => {
  const { attribute = 'rental.stocklist_item.product_type', e2eInstanceId } =
    props

  const intl = useIntl()

  const { items, refine } = useRefinementList({
    attribute,
    limit: 100,
    sortBy: ['name:asc'],
  })

  const { refine: clearRefine } = useClearRefinements({
    includedAttributes: [attribute],
  })

  const currentRefinement = useCurrentRefinement(attribute)

  const refinedValues = useMemo(
    () => new Set(currentRefinement.map(item => item.value.toString())),
    [currentRefinement],
  )

  const refinedNonCoreValues = useMemo(
    () => getNonCoreValues(refinedValues),
    [refinedValues],
  )

  const facetCountsByValue = useMemo(() => {
    const map = new Map<string, number>()

    for (const item of items) {
      if (!item.label) {
        continue
      }

      map.set(item.value.toString(), item.count ?? 0)
    }

    return map
  }, [items])

  const nonCoreFacetValues = useMemo(
    () =>
      getNonCoreValues(
        items.filter(item => item.label).map(item => item.value.toString()),
      ),
    [items],
  )

  const nonCoreFacetCount = useMemo(() => {
    let sum = 0

    for (const value of nonCoreFacetValues) {
      sum += facetCountsByValue.get(value) ?? 0
    }

    return sum
  }, [facetCountsByValue, nonCoreFacetValues])

  const isOtherSelected = refinedNonCoreValues.length > 0

  const visibleCoreValues = useMemo(() => {
    const availableCoreValues = new Set(
      items
        .filter(item => item.label)
        .map(item => item.value.toString())
        .filter(value => CORE_SET.has(value)),
    )

    const refinedCoreValues = [...refinedValues].filter(value =>
      CORE_SET.has(value),
    )

    return new Set<string>([...availableCoreValues, ...refinedCoreValues])
  }, [items, refinedValues])

  const shouldShowOtherOption =
    nonCoreFacetValues.length > 0 || refinedNonCoreValues.length > 0

  const options = useMemo<RefinementListItem[]>(() => {
    const nextOptions: RefinementListItem[] = []

    for (const coreValue of RENTALS_DEVICE_CATEGORY_CORE_VALUES) {
      if (!visibleCoreValues.has(coreValue)) {
        continue
      }

      nextOptions.push({
        count: facetCountsByValue.get(coreValue) ?? 0,
        isRefined: refinedValues.has(coreValue),
        label: intl.formatMessage({
          id: getRentalsDeviceCategoryTranslationId(coreValue),
        }),
        value: coreValue,
      })
    }

    if (shouldShowOtherOption) {
      nextOptions.push({
        count: nonCoreFacetCount,
        isRefined: isOtherSelected,
        label: intl.formatMessage({
          id: 'RentalsDeviceCategory_other',
        }),
        value: RENTALS_DEVICE_CATEGORY_OTHER_VALUE,
      })
    }

    return nextOptions
  }, [
    facetCountsByValue,
    intl,
    isOtherSelected,
    nonCoreFacetCount,
    refinedValues,
    shouldShowOtherOption,
    visibleCoreValues,
  ])

  const selectedValue = useMemo<MultiValue<RefinementListItem>>(() => {
    const selected: RefinementListItem[] = []

    for (const option of options) {
      if (option.value === RENTALS_DEVICE_CATEGORY_OTHER_VALUE) {
        if (isOtherSelected) {
          selected.push(option)
        }

        continue
      }

      if (refinedValues.has(option.value.toString())) {
        selected.push(option)
      }
    }

    return selected
  }, [isOtherSelected, options, refinedValues])

  const handleRefineCore = useCallback(
    (value: string) => {
      scrollToTop()
      refine(value)
    },
    [refine],
  )

  const handleSelectOther = useCallback(() => {
    scrollToTop()

    for (const value of nonCoreFacetValues) {
      if (!refinedValues.has(value)) {
        refine(value)
      }
    }
  }, [nonCoreFacetValues, refine, refinedValues])

  const handleDeselectOther = useCallback(() => {
    scrollToTop()

    for (const value of refinedNonCoreValues) {
      refine(value)
    }
  }, [refine, refinedNonCoreValues])

  const handleClear = useCallback(() => {
    scrollToTop()
    clearRefine()
  }, [clearRefine])

  const handleChange = useCallback(
    (
      newValue: MultiValue<RefinementListItem>,
      actionMeta: ActionMeta<RefinementListItem>,
    ) => {
      if (actionMeta.action === 'clear') {
        handleClear()

        return
      }

      const wantsOtherSelected = newValue.some(
        option => option.value === RENTALS_DEVICE_CATEGORY_OTHER_VALUE,
      )

      if (wantsOtherSelected && !isOtherSelected) {
        handleSelectOther()
      }

      if (!wantsOtherSelected && isOtherSelected) {
        handleDeselectOther()
      }

      if (actionMeta.option && actionMeta.option.value !== undefined) {
        const value = actionMeta.option.value.toString()

        if (value !== RENTALS_DEVICE_CATEGORY_OTHER_VALUE) {
          handleRefineCore(value)
        }
      }

      if (
        actionMeta.removedValue &&
        actionMeta.removedValue.value !== undefined
      ) {
        const value = actionMeta.removedValue.value.toString()

        if (value !== RENTALS_DEVICE_CATEGORY_OTHER_VALUE) {
          handleRefineCore(value)
        }
      }
    },
    [
      handleClear,
      handleDeselectOther,
      handleRefineCore,
      handleSelectOther,
      isOtherSelected,
    ],
  )

  useEffect(() => {
    if (!isOtherSelected) {
      return
    }

    // Keep "Other" live: Add newly appearing non-core facet values
    for (const value of nonCoreFacetValues) {
      if (!refinedValues.has(value)) {
        refine(value)
      }
    }
  }, [isOtherSelected, nonCoreFacetValues, refine, refinedValues])

  if (options.length === 0) {
    return null
  }

  return (
    <div className="Filter-input-container">
      <Label>
        {intl.formatMessage({
          id: 'typesenseAttribute_rental_stocklist_item_device_category',
        })}
      </Label>

      <Select
        iconProps={{
          icon: faTags,
          size: 20,
        }}
        instanceId={e2eInstanceId}
        isClearable
        isMulti
        isSearchable={false}
        onChange={handleChange}
        options={options}
        value={selectedValue}
      />
    </div>
  )
}
